import { RcpFormValue } from './forms/document-form-rcp/document-form-rcp.component';
import { computed, inject, Injectable, signal } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { DemandeValidationDocumentInput } from '@lib/models/demande-validation-document-input.model';
import {
  DOCUMENT_TYPE_CONFIGS,
  DocumentTypeConfig,
} from '@lib/models/document-type-config.model';
import { DocumentConsultantType } from '@lib/models/documents/document-consultant-type';
import {
  getDemandeValidationPjTypeByDocType,
  isPieceJustificative,
  PieceJustificativeDocumentType,
} from '@lib/models/demande-validation-pj/types/piece-justificative-document-types.type';
import {
  DemandeValidationPjAttVigDefInput,
  DemandeValidationPjCerfaDefInput,
  DemandeValidationPjDraftDefInput,
  DemandeValidationPjKbisDefInput,
  DemandeValidationPjNotifAffDefInput,
  DemandeValidationPjNotifDelaiUrssafDefInput,
  DemandeValidationPjRcpDefInput,
  DemandeValidationPjRsacDefInput,
  DemandeValidationPjSyntUrssafDefInput,
} from '@graphql/generated';
import { DemandeValidationPjTypeMapper } from '@lib/mappers/graphql/demande-validation-pj-type-mapper';
import { RsacFormValue } from './forms/document-form-rsac/document-form-rsac.component';
import { RibFormValue } from './forms/document-form-rib/document-form-rib.component';
import { KbisFormValue } from './forms/document-form-kbis/document-form-kbis.component';
import { DemandeValidationPj } from '@lib/models/demande-validation-pj/demande-validation-pj.model';
import { AttVigFormValue } from './forms/document-form-att-vig/document-form-att-vig.component';
import { Cerfa3558FormValue } from './forms/document-form-cerfa-3558/document-form-cerfa-3558.component';
import { Cerfa3666FormValue } from './forms/document-form-cerfa-3666/document-form-cerfa-3666.component';
import { NotifAffFormValue } from './forms/document-form-notif-aff/document-form-notif-aff.component';
import { DocumentBoSectionType } from '@lib/models/documents/document-consultant-section';
import { SyntUrssafFormValue } from './forms/document-form-synt-urssaf/document-form-synt-urssaf.component';
import { NotifDelaiUrssafFormValue } from './forms/document-form-notif-delai-urssaf/document-form-notif-delai-urssaf.component';
import { HabilitationDocumentSubmission } from '@lib/habilitations/habilitation-document-submission.model';
import { HabilitationType } from '@lib/models/demande-habilitation/types/habilitation-type.type';
import { ConsultantDocumentSubmissionService } from '@lib/services/documents/consultant-document-submission.service';

@Injectable()
export class DocumentViewerConsultantService {
  private readonly documentSubmissionService = inject(
    ConsultantDocumentSubmissionService
  );

  readonly documentType = signal<DocumentConsultantType | undefined>(undefined);

  readonly documentConfig = computed<DocumentTypeConfig<FormGroup> | undefined>(() => {
    const type = this.documentType();
    if (!type) return undefined;

    return DOCUMENT_TYPE_CONFIGS.find(c => c.type === type);
  });

  readonly formComponent = computed(() => this.documentConfig()?.formComponent);

  readonly documentFormValue = signal<unknown | undefined>(undefined);
  readonly documentFormValidity = signal<boolean>(false);
  readonly documentFormLoadedFromValPj = signal<boolean>(false);

  public setDocumentFormValueFromDemande(demandeValidationPj: DemandeValidationPj) {
    switch (this.documentType()) {
      case 'rcp':
        this.documentFormValue.set(demandeValidationPj.rcp);
        break;
      case 'kbis_soc':
      case 'kbis_pl':
        this.documentFormValue.set(demandeValidationPj.kbis);
        break;
      case 'rsac':
        this.documentFormValue.set(demandeValidationPj.rsac);
        break;
      case 'attestation_vigilance_agc':
      case 'attestation_vigilance_pl':
      case 'attestation_vigilance_soc':
        this.documentFormValue.set(demandeValidationPj.attVig);
        break;
      case 'cerfa_3558_agc':
      case 'cerfa_3558_pl':
      case 'cerfa_3558_soc':
      case 'cerfa_3666_agc':
      case 'cerfa_3666_pl':
      case 'cerfa_3666_soc':
        this.documentFormValue.set(demandeValidationPj.cerfa);
        break;
      case 'notification_affiliation_agc':
      case 'notification_affiliation_pl':
      case 'notification_affiliation_soc':
        this.documentFormValue.set(demandeValidationPj.notifAff);
        break;
    }
    this.documentFormLoadedFromValPj.set(true);
  }

  public setDocumentFormValue(value: unknown) {
    this.documentFormValue.set(value);
  }

  public setDocumentFormValidity(validity: boolean) {
    this.documentFormValidity.set(validity);
  }

  public async createOrUpdateDemandeValidationDocument(
    input: DemandeValidationDocumentInput,
    section?: DocumentBoSectionType,
    demandeHabilitationPublicId?: string,
    isHidden = false
  ) {
    const submission = this.createHabilitationDocumentSubmission(
      input.file,
      demandeHabilitationPublicId,
      isHidden,
      'assurance'
    );

    return this.documentSubmissionService.saveDemandeValidationPj(
      submission.file,
      submission.documentType,
      submission.draft,
      input.demandeValidationPublicId ?? null,
      section ?? null,
      input.codeConsultant ?? null
    );
  }

  public createHabilitationDocumentSubmission(
    file: File | undefined,
    demandeHabilitationPublicId?: string,
    isHidden = false,
    habilitationType: HabilitationType = 'assurance'
  ): HabilitationDocumentSubmission {
    const typeDocument = this.documentType();
    if (!typeDocument) {
      throw new Error('Aucun type de document sélectionné.');
    }
    if (!isPieceJustificative(typeDocument)) {
      throw new Error("Ce type de document n'est pas pris en charge pour validation.");
    }

    const documentType = typeDocument as PieceJustificativeDocumentType;
    const draft = this.getDemandeValidationPjFormsValueToGraphql(documentType);
    if (demandeHabilitationPublicId) {
      draft.demandeHabilitationPublicId = demandeHabilitationPublicId;
      draft.isHidden = isHidden;
    }

    return { file, documentType, draft, habilitationType };
  }

  private getDemandeValidationPjFormsValueToGraphql(
    documentType: PieceJustificativeDocumentType
  ): DemandeValidationPjDraftDefInput {
    const config = this.documentConfig();

    const result: DemandeValidationPjDraftDefInput = {
      demandeValidationPjType: DemandeValidationPjTypeMapper.toGraphQl(
        getDemandeValidationPjTypeByDocType(documentType)
      ),
    };

    const value = this.documentFormValue();

    switch (config?.type) {
      //TODO RIB
      // case 'rib':
      //   result.rib = this.mapRib(value as RibFormValue);
      //   throw 'Not implemented';
      case 'rcp':
        result.rcp = this.mapRcp(value as RcpFormValue);
        break;
      case 'rsac':
        result.rsac = this.mapRsac(value as RsacFormValue);
        break;
      case 'kbis_pl':
      case 'kbis_soc':
        result.kbis = this.mapKbis(value as KbisFormValue);
        break;
      case 'attestation_vigilance_agc':
      case 'attestation_vigilance_pl':
      case 'attestation_vigilance_soc':
        result.attVig = this.mapAttVig(value as AttVigFormValue);
        break;
      case 'cerfa_3558_agc':
      case 'cerfa_3558_pl':
      case 'cerfa_3558_soc':
        result.cerfa = this.mapCerfa3558(value as Cerfa3558FormValue);
        break;
      case 'cerfa_3666_agc':
      case 'cerfa_3666_pl':
      case 'cerfa_3666_soc':
        result.cerfa = this.mapCerfa3666(value as Cerfa3666FormValue);
        break;
      case 'notification_affiliation_agc':
      case 'notification_affiliation_pl':
      case 'notification_affiliation_soc':
        result.notifAff = this.mapNotifAff(value as NotifAffFormValue);
        break;
      case 'notif_demande_delai_urssaf':
        result.notifDelaiUrssaf = this.mapNotifDelaiUrssaf(
          value as NotifDelaiUrssafFormValue
        );
        break;
      case 'capture_synthese_compte_urssaf':
        result.syntUrssaf = this.mapSyntUrssaf(value as SyntUrssafFormValue);
        break;
      default:
        break;
    }

    return result;
  }

  // TODO Mappers

  private mapRib(value: RibFormValue) {
    //TODO RIB quand réactivé côté backend
  }

  private mapRcp(value: RcpFormValue): DemandeValidationPjRcpDefInput {
    return {
      statutJuridiqueId: value.statutJuridique ?? [],
      assureurId: value.assureurId!.toString(),
      rcpType: value.rcpType!.map(rcpType => rcpType.toString()),
      dateDebutValidite: value.dateDebutValidite,
    };
  }

  private mapRsac(value: RsacFormValue): DemandeValidationPjRsacDefInput {
    return {
      voie: value.voie,
      codePostal: value.codePostal!,
      ville: value.ville,
      pays: value.pays,
      nomCommercial: value.nomCommercial,
      numeroSiret: value.numeroSiret,
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }

  private mapKbis(value: KbisFormValue): DemandeValidationPjKbisDefInput {
    return {
      voie: value.voie,
      codePostal: value.codePostal,
      ville: value.ville,
      pays: value.pays,
      capitalSocial: value.capitalSocial,
      formeJuridique: value.formeJuridique,
      raisonSociale: value.raisonSociale,
      villeRegistreCommerce: value.villeRegistreCommerce,
      numeroSiret: value.numeroSiret,
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
      numeroSiren: value.numeroSiren,
      numeroTva: value.numeroTva,
    };
  }

  private mapAttVig(value: AttVigFormValue): DemandeValidationPjAttVigDefInput {
    return {
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }

  private mapCerfa3558(value: Cerfa3558FormValue): DemandeValidationPjCerfaDefInput {
    return {
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }

  private mapCerfa3666(value: Cerfa3666FormValue): DemandeValidationPjCerfaDefInput {
    return {
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }

  private mapNotifAff(value: NotifAffFormValue): DemandeValidationPjNotifAffDefInput {
    return {
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }

  private mapSyntUrssaf(
    value: SyntUrssafFormValue
  ): DemandeValidationPjSyntUrssafDefInput {
    return {
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }

  private mapNotifDelaiUrssaf(
    value: NotifDelaiUrssafFormValue
  ): DemandeValidationPjNotifDelaiUrssafDefInput {
    return {
      dateDebutValidite: value.dateDebutValidite,
      dateFinValidite: value.dateFinValidite,
    };
  }
}
